import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from 'react-router-dom';
import { CircularProgress, Container } from '@mui/material';
import { routes as adminCampaignsRoutes } from 'src/modules/adminCampaigns/routes';
import { routes as adminSignupsRoutes } from 'src/modules/adminSignups/routes';
import { routes as applicationsRoutes } from 'src/modules/applications/routes';
import { routes as blocklistRoutes } from 'src/modules/blocklist/routes';
import { routes as profileRoutes } from 'src/modules/profile/routes';
import { routes as campaignsRoutes } from 'src/modules/campaigns/routes';
import { routes as campaignBuilderV2Routes } from 'src/modules/campaign-builder-v2/routes';
import { routes as campaignConfigBuilderV2Routes } from 'src/modules/campaignConfigBuilder/routes';
import { routes as calendarRoutes } from 'src/modules/calendar/routes';
import { routes as companiesRoutes } from 'src/modules/companies/routes';
import { routes as contactsRoutes } from 'src/modules/contacts/routes';
import { routes as groupsRoutes } from 'src/modules/groups/routes';
import { routes as homeRoutes } from 'src/modules/home/routes';
import { routes as keywordsRoutes } from 'src/modules/keywords/routes';
import { routes as linkTrackingRoutes } from 'src/modules/shortLinks/routes';
import { routes as plansRoutes } from 'src/modules/plans/routes';
import { routes as canvaRoutes } from 'src/modules/canva/routes';
import { routes as assetLibraryRoutes } from 'src/modules/assetLibrary/routes';
import { routes as reportsRoutes } from 'src/modules/reports/routes';
import { routes as qrCodesRoutes } from 'src/modules/qrCodes/routes';
import { routes as surveyResponsesRoutes } from 'src/modules/surveyResponses/routes';
import { routes as automationsRoutes } from 'src/modules/automations/routes';
import { routes as authenticationRoutes } from 'src/modules/authentication/routes';
import { routes as settingsRoutes } from 'src/modules/settings/routes';
import { routes as signupRoutes } from 'src/modules/signup/routes';
import { routes as inboxRoutes } from 'src/modules/inbox/routes';
import { routes as contactCaptureRoutes } from 'src/modules/contactCapture/routes';

import PrivateRoute from 'src/routes/PrivateRoute';
import { ShortUrlResolver } from 'src/modules/shortLinks/components';
import AuthenticatedLayout from './AuthenticatedLayout';
import RouteErrorBoundary from './RouteErrorBoundary';

const Onboarding = lazy(() =>
  import('src/modules/onboarding/pages/view').then((module) => ({ default: module.Onboarding })),
);
const OnboardingCompleted = lazy(() =>
  import('src/modules/onboarding/pages/completed').then((module) => ({ default: module.OnboardingCompleted })),
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RouteErrorBoundary />}>
      {authenticationRoutes}
      {signupRoutes}
      <Route
        path="/onboarding"
        element={
          <PrivateRoute>
            <Suspense
              fallback={
                <Container maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Container>
              }
            >
              <Onboarding />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route
        path="/onboarding/completed"
        element={
          <PrivateRoute>
            <Suspense
              fallback={
                <Container maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Container>
              }
            >
              <OnboardingCompleted />
            </Suspense>
          </PrivateRoute>
        }
      />
      <Route element={<AuthenticatedLayout />}>
        {homeRoutes}
        {groupsRoutes}
        {contactsRoutes}
        {assetLibraryRoutes}
        {qrCodesRoutes}
        {contactCaptureRoutes}
        {surveyResponsesRoutes}
        {campaignsRoutes}
        {campaignBuilderV2Routes}
        {campaignConfigBuilderV2Routes}
        {calendarRoutes}
        {keywordsRoutes}
        {automationsRoutes}
        {linkTrackingRoutes}
        {settingsRoutes}
        {profileRoutes}
        {companiesRoutes}
        {plansRoutes}
        {applicationsRoutes}
        {adminCampaignsRoutes}
        {adminSignupsRoutes}
        {blocklistRoutes}
        {reportsRoutes}
        <Route
          path="/appointments"
          element={<Navigate to="/calendar" replace />}
        />
        {inboxRoutes}
      </Route>
      {canvaRoutes}
      <Route
        path=":code"
        element={<ShortUrlResolver />}
      />
    </Route>,
  ),
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);

export default router;
